<template>
    <!-- Overlay -->
    <div v-if="drawer" class="custom-overlay" @click="closeDrawer"></div>

    <!-- Right-side drawer -->
    <v-navigation-drawer v-model="drawer" location="right" temporary width="400" class="manage-users-drawer custom-drawer">
        <v-card flat>
            <!-- Header with Close Button -->
            <v-card-title class="d-flex justify-space-between align-center pa-0">
                <span class="text-h6 pa-0">{{ isEditMode ? "Edit User" : "Add User" }}</span>
                <v-btn variant="text" @click="closeDrawer" class="userCloseBtn">
                    <v-icon class="userClose">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider></v-divider>
            <v-divider></v-divider>
            <br/>

            <v-card-text class="pt-2">
                <v-window v-model="tab">
                    <v-window-item value="user">
                        <label>First Name</label>
                        <v-text-field
                            v-model="user.firstName"
                            :rules="firstNameRules"
                            required
                            variant="solo"
                            class="custom-field"
                            dense
                            outlined
                            clearable
                            placeholder="First Name"
                        ></v-text-field>

                        <label>Last Name</label>
                        <v-text-field
                            v-model="user.lastName"
                            :rules="lastNameRules"
                            required
                            variant="solo"
                            class="custom-field"
                            dense
                            outlined
                            clearable
                            placeholder="Last Name"
                        ></v-text-field>

                        <label>Username/Email Address</label>
                        <v-text-field
                            v-model="user.username"
                            :rules="usernameRules"
                            required
                            variant="solo"
                            class="custom-field"
                            dense
                            outlined
                            placeholder="Username/Email Address"
                            :readonly="isEditMode"
                        ></v-text-field>

                        <label>Role</label>
                        <v-select
                            v-model="user.role"
                            :items="roles"
                            :rules="roleRules"
                            required
                            variant="solo"
                            item-title="name"
                            item-value="name"
                            clearable
                            placeholder="Role"
                            class="custom-y-axis-chips custom-field mb-2"
                        ></v-select>

                        <label v-if="!isEditMode">Password</label>
                        <v-text-field
                            v-if="!isEditMode"
                            v-model="user.password"
                            :rules="passwordRules"
                            required
                            type="password"
                            variant="solo"
                            class="custom-field"
                            dense
                            outlined
                            clearable
                            placeholder="User password"
                            autocomplete="new-password"
                        ></v-text-field>

                        <label>Tenants</label>
                        <v-select
                            v-model="user.tenantIDs"
                            :items="tenants"
                            :rules="tenantsRules"
                            required
                            multiple
                            chips
                            clearable
                            closable-chips
                            variant="solo"
                            item-title="name"
                            item-value="value"
                            placeholder="Tenants"
                            class="custom-y-axis-chips custom-field"
                        ></v-select>
                    </v-window-item>
                </v-window>
            </v-card-text>

            <div class="d-flex">
                <v-col cols="6" class="pa-0 pt-3 pr-1">
                    <v-btn block flat class="reset-btn text-none" @click="closeDrawer">
                        <span>Cancel</span>
                    </v-btn>
                </v-col>
                <v-col cols="6" class="pa-0 pt-3 pl-1">
                    <v-btn
                        block
                        flat
                        class="save-btn filter-wrap-btn text-none"
                        :loading="createLoading"
                        :disabled="isSaveDisabled"
                        @click="saveUser"
                    >
                        <v-img
                            :src="save"
                            alt="save"
                            width="15px"
                            height="15px"
                            class="mr-2"
                        ></v-img>
                        <span>{{ isEditMode ? "Update" : "Save" }}</span>
                    </v-btn>
                </v-col>
            </div>
        </v-card>
    </v-navigation-drawer>

    <v-snackbar
        v-model="snackbar.visible"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
    >
        {{ snackbar.message }}
    </v-snackbar>
</template>

<script>
import api from "../../../axiosInterceptor";

export default {
    props: {
        modelValue: Boolean,
        editUser: Object,
    },
    data() {
        return {
            tab: "user",
            save: require("../../../assets/Images/save btn.png"),
            user: {
                firstName: "",
                lastName: "",
                username: "",
                password: "",
                tenantIDs: [],
                role: null,
            },
            roles: [],
            tenants: [],
            createLoading: false,
            snackbar: {
                visible: false,
                message: "",
                color: "success",
                timeout: 3000,
            },
        };
    },
    computed: {
        drawer: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
        isEditMode() {
            return this.editUser && Object.keys(this.editUser).length > 0;
        },
        firstNameRules() {
            return [v => !!v || "First Name is required"];
        },
        lastNameRules() {
            return [v => !!v || "Last Name is required"];
        },
        usernameRules() {
            return [v => !!v || "Username is required"];
        },
        roleRules() {
            return [v => !!v || "Role is required"];
        },
        passwordRules() {
            return this.isEditMode ? [] : [v => !!v || "Password is required"];
        },
        tenantsRules() {
            return [v => (v && v.length > 0) || "Select at least one Tenant"];
        },
        isSaveDisabled() {
            const u = this.user;
            if (!u.firstName || !u.lastName || !u.username || !u.role) return true;
            if (!this.isEditMode && !u.password) return true;
            if (!u.tenantIDs || u.tenantIDs.length === 0) return true;
            return false;
        },
    },
    watch: {
        editUser: {
            immediate: true,
            handler(newUser) {
                if (newUser && Object.keys(newUser).length > 0) {
                    this.user = { ...newUser };
                } else {
                    this.resetForm();
                }
            },
        },
    },
    methods: {
        showSnackbar(message, color = "success") {
            this.snackbar.message = message;
            this.snackbar.color = color;
            this.snackbar.visible = true;
        },
        async fetchRoles() {
            try {
                const response = await api.get("/public/api/admin/roles");
                if (response.data.statusCode === 200) {
                    this.roles = response.data.data.map(role => ({
                        name: role.name,
                        value: role.id,
                    }));
                }
            } catch (error) {
                console.error("API error:", error);
            }
        },
        async fetchTenants() {
            try {
                const response = await api.get("/public/api/admin/tenants");
                if (response.data.statusCode === 200) {
                    this.tenants = response.data.data.tenants.map(tenant => ({
                        name: tenant.name,
                        value: tenant.ID,
                    }));
                }
            } catch (error) {
                console.error("API error:", error);
            }
        },
        async saveUser() {
            if (this.isSaveDisabled) {
                this.showSnackbar("Please fill out all required fields", "error");
                return;
            }
            this.createLoading = true;
            try {
                if (this.isEditMode) {
                    const response = await api.put(
                        `/public/api/admin/users/${this.user.id}`,
                        { data: { user: this.user } }
                    );
                    if (response.data.statusCode === 200) {
                        this.showSnackbar("User updated successfully!", "success");
                        this.$emit("user-added");
                        this.closeDrawer();
                    } else {
                        this.showSnackbar(response.data.message, "error");
                    }
                } else {
                    const response = await api.post(
                        "/public/api/admin/users",
                        { data: { user: this.user } }
                    );
                    if (response.data.statusCode === 200) {
                        this.showSnackbar("User created successfully!", "success");
                        this.$emit("user-added");
                        this.closeDrawer();
                    } else {
                        this.showSnackbar(response.data.message, "error");
                    }
                }
            } catch (error) {
                this.showSnackbar(
                    error.response?.data?.message || "An error occurred",
                    "error"
                );
            } finally {
                this.createLoading = false;
            }
        },
        closeDrawer() {
            this.$emit("update:editUser", null);
            this.drawer = false;
        },
        resetForm() {
            this.user = {
                firstName: "",
                lastName: "",
                username: "",
                password: "",
                tenantIDs: [],
                role: null,
            };
        },
    },
    mounted() {
        this.fetchRoles();
        this.fetchTenants();
    },
};
</script>

<style scoped>
.custom-overlay {
    position: fixed;
    top: 64px;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1050;
}

.custom-drawer {
    z-index: 1100 !important;
}

.userCloseBtn {
    padding: 0 !important;
    width: auto !important;
    min-width: 0 !important;
}
</style>